import type { AlphaStylePick, MbcBrandContext } from "@nlc/shared-types";

import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/button";
import { useSubmitAlphaCreation } from "@/features/request/alpha/hooks/useSubmitAlphaCreation";
import { startAlphaStepTransition } from "@/features/request/alpha/layout/alphaStepTransition";
import { AlphaStylesStep } from "@/features/request/alpha/styles/AlphaStylesStep";
import {
  ALPHA_PLAN_COUNT,
  dropMissingUploads,
} from "@/features/request/alpha/styles/stylePicks";
import { NOT_STARTED_QUALITY } from "@/features/request/mbc-review/lib/brief-score-summary";
import { isTeamUser } from "@/features/requests/utils/roleUtils";
import { useUser } from "@/hooks/useUser";

import type { MbcRequestType } from "../lib/request-draft-api";
import type { MbcSessionIds } from "../lib/session-ids";

import { useBriefMemory } from "../hooks/useBriefMemory";
import { useLeaveMbcFlow } from "../hooks/useLeaveMbcFlow";
import { useMBCChat } from "../hooks/useMBCChat";
import { patchBrief } from "../lib/brief-api";
import { discardSession } from "../lib/draft-api";
import { buildGatePreamble } from "../lib/gate-preamble";
import { requestIdFromSessionId } from "../lib/session-ids";
import { AlphaBriefSheet } from "./AlphaBriefSheet";
import { AlphaStepFrame } from "./AlphaStepFrame";
import { BriefSidebar } from "./BriefSidebar";
import { DiscardBriefDialog } from "./DiscardBriefDialog";
import { MBCBriefSheet } from "./MBCBriefSheet";
import { MBCChatHeader } from "./MBCChatHeader";
import { MBCChatPanel } from "./MBCChatPanel";
import { readGroup } from "./upload-assets/types";
import { UploadAssetsModal } from "./upload-assets/UploadAssetsModal";

type MBCConversationProps = {
  brand: MbcBrandContext | null;
  designType?: number;
  isAlpha?: boolean;
  projectId?: number;
  requestType?: MbcRequestType;
  resume?: MbcSessionIds;
};

export function MBCConversation({
  brand,
  designType,
  isAlpha = false,
  projectId,
  requestType,
  resume,
}: MBCConversationProps) {
  const navigate = useNavigate();
  const { data: userData } = useUser();
  const isTeamMember = isTeamUser(userData?.user?.roleName);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isBriefOpen, setIsBriefOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [alphaStep, setAlphaStep] = useState<"Prompt" | "Styles">("Prompt");
  const [stylePicks, setStylePicks] = useState<AlphaStylePick[]>([]);
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const trackUpload = useCallback(
    (delta: -1 | 1) => setUploadsInFlight((count) => count + delta),
    [],
  );

  const {
    error,
    isStreaming,
    messages,
    refreshSignal,
    resourceId,
    sendUserMessage,
    status,
    threadId,
  } = useMBCChat({
    brandId: brand?.id,
    designTypeId: designType,
    projectId,
    requestType,
    resume,
  });

  // Before the first send, no request exists yet (T8) so resourceId/threadId
  // are undefined — useBriefMemory requires strings, and an empty id just
  // fetches nothing meaningful until the real ids land on the next render.
  const briefMemory = useBriefMemory({
    refreshSignal,
    resourceId: resourceId ?? "",
    threadId: threadId ?? "",
  });

  const alphaSubmit = useSubmitAlphaCreation(
    threadId ? requestIdFromSessionId(threadId) : null,
  );

  const styleUploads = useMemo(
    () => readGroup(briefMemory.memory?.inspirations).files,
    [briefMemory.memory],
  );
  const submitWithPicks = () =>
    alphaSubmit.submit(
      dropMissingUploads(
        stylePicks,
        styleUploads.map((file) => file.attachmentId),
      ),
    );

  const canReview = !isAlpha && briefMemory.quality?.canSubmit === true;
  const canContinueToStyles =
    briefMemory.quality?.canSubmit === true && uploadsInFlight === 0;
  const isScoreCalculating = isStreaming || briefMemory.isSettling;
  // Before the first send there is no request, so no brief to fetch a score for.
  const quality = threadId ? briefMemory.quality : NOT_STARTED_QUALITY;

  // Publish the session ids to the URL so closing/refreshing the page can come
  // back to this thread instead of silently starting a new one. `replace` keeps
  // it out of the history stack; the ref keeps it to a single write per thread.
  const syncedThreadId = useRef<null | string>(null);

  useEffect(() => {
    // Resume mode already came from the URL — nothing to write back.
    if (resume) return;
    if (!threadId || !resourceId || syncedThreadId.current === threadId) return;
    syncedThreadId.current = threadId;

    void navigate({
      replace: true,
      search: {
        brandId: brand?.id,
        designType,
        ["project-id"]: projectId,
        ["request-type"]: requestType,
        resourceId,
        threadId,
      },
      to: "/requests/mbc",
    });
  }, [
    brand?.id,
    designType,
    navigate,
    projectId,
    requestType,
    resume,
    resourceId,
    threadId,
  ]);

  const handleReview = useCallback(() => {
    void navigate({
      replace: true,
      search: {
        brandId: brand?.id,
        designType,
        ["project-id"]: projectId,
        ["request-type"]: requestType,
        resourceId,
        threadId,
      },
      to: "/requests/mbc-review",
    });
  }, [
    brand?.id,
    designType,
    navigate,
    projectId,
    requestType,
    resourceId,
    threadId,
  ]);

  const handleRename = useCallback(
    (title: string) => {
      if (!threadId) return;

      void patchBrief(threadId, resourceId, { title })
        .catch(() => {})
        .then(() => briefMemory.refetch());
    },
    [briefMemory, resourceId, threadId],
  );

  const handleBack = useLeaveMbcFlow(isAlpha);

  // Switch to the Classic Brief Form: discard the MBC session, then redirect.
  const confirmSwitchToClassic = useCallback(async () => {
    setIsDiscarding(true);
    try {
      if (threadId) await discardSession(threadId);

      // `replace` drops the /requests/mbc?threadId=… entry this session wrote,
      // so Back can't land on a thread that no longer exists.
      void navigate({
        replace: true,
        search: {
          designType,
          ["project-id"]: projectId,
          ["request-type"]: requestType ?? "graphic",
        },
        to: "/requests/create",
      });
    } finally {
      setIsDiscarding(false);
      setIsDiscardOpen(false);
    }
  }, [designType, navigate, projectId, requestType, threadId]);

  // The gate steps are never sent to the agent, so they are rebuilt in front
  // of the persisted transcript — on a fresh start and on resume alike.
  const displayMessages = useMemo(
    () => [...buildGatePreamble(brand, isAlpha), ...messages],
    [brand, isAlpha, messages],
  );

  const upload = {
    memory: briefMemory.memory,
    onFilesChanged: () => void briefMemory.recheckFiles(),
    onUploaded: () => void briefMemory.refetch(),
    onUploadingChange: trackUpload,
    resourceId,
    threadId,
  };

  if (isAlpha) {
    const goToStep = (
      step: "Prompt" | "Styles",
      direction: "back" | "forward",
    ) => startAlphaStepTransition(() => setAlphaStep(step), direction);

    return (
      <>
        <AlphaBriefSheet
          canSubmit={briefMemory.quality?.canSubmit === true}
          isOpen={isBriefOpen}
          isSubmitting={alphaSubmit.isSubmitting}
          memory={briefMemory.memory}
          onBriefSaved={() => void briefMemory.refetch()}
          onOpenChange={setIsBriefOpen}
          onSubmit={submitWithPicks}
          resourceId={resourceId}
          submitError={alphaSubmit.errorMessage}
          threadId={threadId}
        />

        <AlphaStepFrame
          onBack={
            alphaStep === "Prompt"
              ? handleBack
              : () => goToStep("Prompt", "back")
          }
          onViewBrief={() => setIsBriefOpen(true)}
          primaryAction={
            alphaStep === "Prompt" ? (
              <Button
                isDisabled={!canContinueToStyles}
                onPress={() => goToStep("Styles", "forward")}
                variant="primary"
              >
                Continue to styles
                <ArrowRight className="size-4" />
              </Button>
            ) : (
              <Button
                isLoading={alphaSubmit.isSubmitting}
                onPress={submitWithPicks}
                variant="primary"
              >
                <Sparkles className="size-4" />
                Generate {ALPHA_PLAN_COUNT} ads
              </Button>
            )
          }
          step={alphaStep}
        >
          {alphaStep === "Prompt" ? (
            <>
              <div className="mx-auto flex min-h-0 w-full max-w-[860px] flex-1 p-md lg:p-3xl">
                <MBCChatPanel
                  isAlpha
                  isStreaming={isStreaming}
                  isUploading={uploadsInFlight > 0}
                  messages={displayMessages}
                  onReview={handleReview}
                  onSelectChoice={sendUserMessage}
                  onSend={sendUserMessage}
                  status={status}
                  upload={upload}
                />
              </div>
              {error ? (
                <p className="px-md pb-lg text-sm lg:px-3xl text-text-error-primary">
                  Something went wrong. Please refresh and try again.
                </p>
              ) : null}
            </>
          ) : (
            <AlphaStylesStep
              errorMessage={alphaSubmit.errorMessage}
              onPicksChange={setStylePicks}
              picks={stylePicks}
              uploads={styleUploads}
            />
          )}
        </AlphaStepFrame>
      </>
    );
  }

  return (
    <>
      <MBCChatHeader
        brandId={brand?.id}
        designType={designType}
        isTeamMember={isTeamMember}
        onBack={handleBack}
        onRename={threadId ? handleRename : undefined}
        onSwitchToClassic={() => setIsDiscardOpen(true)}
        requestName={briefMemory.memory?.title || undefined}
      />

      <MBCBriefSheet
        canAddFiles={Boolean(threadId)}
        isCalculating={isScoreCalculating}
        memory={briefMemory.memory}
        onAddAssets={() => setIsUploadOpen(true)}
        onFilesChanged={() => void briefMemory.recheckFiles()}
        quality={quality}
      />

      <div className="flex min-h-0 flex-1 gap-3xl p-md lg:p-3xl">
        <MBCChatPanel
          canReview={canReview}
          isStreaming={isStreaming}
          isUploading={uploadsInFlight > 0}
          messages={displayMessages}
          onAttach={() => setIsUploadOpen(true)}
          onReview={handleReview}
          onSelectChoice={sendUserMessage}
          onSend={sendUserMessage}
          status={status}
          upload={upload}
        />

        <BriefSidebar
          canAddFiles={Boolean(threadId)}
          isCalculating={isScoreCalculating}
          memory={briefMemory.memory}
          onAddAssets={() => setIsUploadOpen(true)}
          onFilesChanged={() => void briefMemory.recheckFiles()}
          quality={quality}
        />
      </div>

      {error ? (
        <p className="px-md pb-lg text-sm lg:px-3xl text-text-error-primary">
          Something went wrong. Please refresh and try again.
        </p>
      ) : null}

      <UploadAssetsModal
        isOpen={isUploadOpen}
        memory={briefMemory.memory}
        onBriefChanged={() => void briefMemory.refetch()}
        onFilesChanged={() => void briefMemory.recheckFiles()}
        onOpenChange={setIsUploadOpen}
        onUploadingChange={trackUpload}
        resourceId={resourceId}
        threadId={threadId}
      />

      <DiscardBriefDialog
        isDiscarding={isDiscarding}
        isOpen={isDiscardOpen}
        onConfirm={() => void confirmSwitchToClassic()}
        onOpenChange={setIsDiscardOpen}
      />
    </>
  );
}
